/**
 * MeshBinary — the container `scripts/buildAssets.mjs` writes for every mesh
 * and `AssetLibrary.mesh` reads back (`public/assets/meshes/<name>.bin`).
 *
 * Why not OBJ: the same 49 meshes as OBJ text weigh 15.8 MB (3.2 MB gzipped)
 * and `OBJLoader` has to tokenise every line on the main thread. As indexed
 * binary they are a quarter of that and decode by slicing typed arrays.
 *
 * Layout — a JSON header over a binary body, the glTF pattern:
 *
 *     bytes 0..3   'MSHB'
 *     bytes 4..7   u32 LE: byte length of the header JSON, padded to 4
 *     bytes 8..    header JSON (UTF-8, space-padded)
 *     then         the body; every offset in the header is relative to it
 *
 * The header is self-describing (`MeshBinaryHeader`): each attribute names
 * its component count and storage format, and the decoder dequantises every
 * format to `Float32Array` so a consumer sees exactly what `OBJLoader` used to
 * hand it — float attributes, in the same order, plus an index and draw groups.
 *
 * Storage formats, and what AST-1 measured to choose them (level 8, 8.2 s,
 * `npm run render` A/B against the OBJ path, `npm run pixdiff`):
 *  - `position`, `normal`, `uv` are **f32**. Positions must be bit-exact.
 *    UVs range to ±160 (the ground plate tiles), so no 16-bit normalised form
 *    fits them. Normals as `snorm8` would save ~0.5 MB but shift the toon ramp
 *    at its threshold — see `claude_progress.md` (AST-1) for the pixel count.
 *  - `color` is **unorm8**: it is the shader's palette **mask**, authored as
 *    UNorm8 in the Unity mesh and only ever 0 or 1 in the shipped meshes, so
 *    `k / 255` reproduces the OBJ path's value bit for bit.
 *
 * This module is imported by node (`buildAssets.mjs`, through node's own type
 * stripping — so only erasable TypeScript syntax here: no enums, no parameter
 * properties) as well as by the browser bundle. It depends on nothing.
 */

export type MeshBinaryFormat = 'f32' | 'unorm8' | 'snorm8' | 'snorm16'

export interface MeshBinaryAttributeHeader {
  name: string
  itemSize: number
  format: MeshBinaryFormat
  /** Relative to the start of the body. */
  byteOffset: number
  byteLength: number
}

export interface MeshBinaryGroup {
  /** First index of the group, in indices. */
  start: number
  /** Number of indices in the group. */
  count: number
  materialIndex: number
}

export interface MeshBinaryHeader {
  version: 1
  vertexCount: number
  /** In the order the geometry should receive them. */
  attributes: MeshBinaryAttributeHeader[]
  index: {
    format: 'u16' | 'u32'
    count: number
    byteOffset: number
    byteLength: number
  }
  /** One per submesh, in submesh order. A single-submesh mesh has one. */
  groups: MeshBinaryGroup[]
}

/** An attribute going in to the encoder. `format` defaults to `f32`. */
export interface MeshDataAttribute {
  name: string
  itemSize: number
  array: Float32Array
  format?: MeshBinaryFormat
}

/** What the encoder takes and what the decoder gives back. */
export interface MeshData {
  vertexCount: number
  attributes: MeshDataAttribute[]
  index: Uint16Array | Uint32Array
  groups: MeshBinaryGroup[]
}

export const MESH_BINARY_MAGIC = 'MSHB'
export const MESH_BINARY_VERSION = 1

const BYTES_PER_COMPONENT: Record<MeshBinaryFormat, number> = {
  f32: 4,
  unorm8: 1,
  snorm8: 1,
  snorm16: 2,
}

const align4 = (n: number): number => (n + 3) & ~3

/**
 * Typed arrays read and write in the platform's byte order, and this codec
 * relies on that being little-endian at both ends — true of every browser and
 * node build the port targets. Refuse to run anywhere else rather than decode
 * garbage silently.
 */
function assertLittleEndian(): void {
  if (new Uint8Array(new Uint16Array([1]).buffer)[0] !== 1) {
    throw new Error('MeshBinary: this platform is big-endian; the codec assumes little-endian')
  }
}

// ---------------------------------------------------------------------------
// Quantisation
// ---------------------------------------------------------------------------

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)

/** Store `values` as `format` into a fresh byte array. */
function quantise(values: Float32Array, format: MeshBinaryFormat): Uint8Array {
  switch (format) {
    case 'f32':
      return new Uint8Array(new Float32Array(values).buffer)
    case 'unorm8': {
      const out = new Uint8Array(values.length)
      for (let i = 0; i < values.length; i++) out[i] = Math.round(clamp(values[i], 0, 1) * 255)
      return out
    }
    case 'snorm8': {
      const out = new Int8Array(values.length)
      for (let i = 0; i < values.length; i++) out[i] = Math.round(clamp(values[i], -1, 1) * 127)
      return new Uint8Array(out.buffer)
    }
    case 'snorm16': {
      const out = new Int16Array(values.length)
      for (let i = 0; i < values.length; i++) out[i] = Math.round(clamp(values[i], -1, 1) * 32767)
      return new Uint8Array(out.buffer)
    }
  }
}

/**
 * Read `count` components stored as `format` back into floats.
 *
 * `bytes` is a fresh copy (see `slice` in the decoder), so its buffer starts
 * at 0 and the typed-array views below are always aligned.
 */
function dequantise(bytes: Uint8Array, format: MeshBinaryFormat, count: number): Float32Array {
  switch (format) {
    case 'f32':
      return new Float32Array(bytes.buffer, bytes.byteOffset, count)
    case 'unorm8': {
      const out = new Float32Array(count)
      for (let i = 0; i < count; i++) out[i] = bytes[i] / 255
      return out
    }
    case 'snorm8': {
      const raw = new Int8Array(bytes.buffer, bytes.byteOffset, count)
      const out = new Float32Array(count)
      // GL's SNORM rule: −128 and −127 both mean −1.
      for (let i = 0; i < count; i++) out[i] = Math.max(raw[i] / 127, -1)
      return out
    }
    case 'snorm16': {
      const raw = new Int16Array(bytes.buffer, bytes.byteOffset, count)
      const out = new Float32Array(count)
      for (let i = 0; i < count; i++) out[i] = Math.max(raw[i] / 32767, -1)
      return out
    }
  }
}

// ---------------------------------------------------------------------------
// Encode
// ---------------------------------------------------------------------------

/**
 * Pack a mesh into the MeshBinary container.
 *
 * Validates the shape before writing anything: every attribute must hold
 * exactly `vertexCount × itemSize` values, every index must address a vertex,
 * and every group must lie inside the index — a bad file is worse than a
 * failed build.
 */
export function encodeMeshBinary(mesh: MeshData): Uint8Array {
  assertLittleEndian()
  const { vertexCount, attributes, index, groups } = mesh
  if (!Number.isInteger(vertexCount) || vertexCount <= 0) {
    throw new Error(`MeshBinary: vertexCount must be a positive integer, got ${vertexCount}`)
  }
  if (attributes.length === 0) throw new Error('MeshBinary: a mesh needs at least one attribute')
  const seen = new Set<string>()
  for (const a of attributes) {
    if (seen.has(a.name)) throw new Error(`MeshBinary: duplicate attribute "${a.name}"`)
    seen.add(a.name)
    if (!Number.isInteger(a.itemSize) || a.itemSize <= 0) {
      throw new Error(`MeshBinary: attribute "${a.name}" has itemSize ${a.itemSize}`)
    }
    if (a.array.length !== vertexCount * a.itemSize) {
      throw new Error(
        `MeshBinary: attribute "${a.name}" has ${a.array.length} values, ` +
          `expected ${vertexCount} × ${a.itemSize}`,
      )
    }
  }
  for (let i = 0; i < index.length; i++) {
    if (index[i] >= vertexCount) {
      throw new Error(`MeshBinary: index[${i}] = ${index[i]} is outside ${vertexCount} vertices`)
    }
  }
  for (const g of groups) {
    if (g.start < 0 || g.count < 0 || g.start + g.count > index.length) {
      throw new Error(
        `MeshBinary: group ${g.start}+${g.count} is outside an index of ${index.length}`,
      )
    }
  }

  // Body: attributes in order, then the index, each region 4-byte aligned.
  const regions: Uint8Array[] = []
  const attributeHeaders: MeshBinaryAttributeHeader[] = []
  let cursor = 0
  const place = (bytes: Uint8Array): { byteOffset: number; byteLength: number } => {
    cursor = align4(cursor)
    const at = { byteOffset: cursor, byteLength: bytes.byteLength }
    regions.push(bytes)
    cursor += bytes.byteLength
    return at
  }
  for (const a of attributes) {
    const format = a.format ?? 'f32'
    attributeHeaders.push({ name: a.name, itemSize: a.itemSize, format, ...place(quantise(a.array, format)) })
  }
  // u16 whenever it fits; the shipped meshes top out at 10 131 vertices.
  const indexFormat: 'u16' | 'u32' = vertexCount <= 0xffff ? 'u16' : 'u32'
  const indexArray = indexFormat === 'u16' ? Uint16Array.from(index) : Uint32Array.from(index)
  const indexAt = place(new Uint8Array(indexArray.buffer))

  const header: MeshBinaryHeader = {
    version: MESH_BINARY_VERSION,
    vertexCount,
    attributes: attributeHeaders,
    index: { format: indexFormat, count: index.length, ...indexAt },
    groups: groups.map((g) => ({ start: g.start, count: g.count, materialIndex: g.materialIndex })),
  }

  const headerBytes = new TextEncoder().encode(JSON.stringify(header))
  const headerLength = align4(headerBytes.byteLength)
  const bodyLength = cursor
  const out = new Uint8Array(8 + headerLength + bodyLength)
  out.set(new TextEncoder().encode(MESH_BINARY_MAGIC), 0)
  new DataView(out.buffer).setUint32(4, headerLength, true)
  out.set(headerBytes, 8)
  for (let i = headerBytes.byteLength; i < headerLength; i++) out[8 + i] = 0x20 // space padding
  const bodyBase = 8 + headerLength
  let at = 0
  for (const region of regions) {
    at = align4(at)
    out.set(region, bodyBase + at)
    at += region.byteLength
  }
  return out
}

// ---------------------------------------------------------------------------
// Decode
// ---------------------------------------------------------------------------

/** Just the header, for tools that want to know what a file holds. */
export function readMeshBinaryHeader(bytes: Uint8Array): {
  header: MeshBinaryHeader
  bodyOffset: number
} {
  if (bytes.byteLength < 8) throw new Error('MeshBinary: file is shorter than its preamble')
  const magic = new TextDecoder().decode(bytes.subarray(0, 4))
  if (magic !== MESH_BINARY_MAGIC) throw new Error(`MeshBinary: bad magic "${magic}"`)
  const headerLength = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(4, true)
  if (8 + headerLength > bytes.byteLength) {
    throw new Error(`MeshBinary: header length ${headerLength} runs past the file`)
  }
  const json = new TextDecoder().decode(bytes.subarray(8, 8 + headerLength))
  const header = JSON.parse(json) as MeshBinaryHeader
  if (header.version !== MESH_BINARY_VERSION) {
    throw new Error(`MeshBinary: version ${header.version}, this decoder reads ${MESH_BINARY_VERSION}`)
  }
  return { header, bodyOffset: 8 + headerLength }
}

/**
 * Unpack a MeshBinary file.
 *
 * Every attribute comes back as `Float32Array` regardless of how it was
 * stored, in header order, so the result can be handed to a geometry exactly
 * the way `OBJLoader`'s output was.
 */
export function decodeMeshBinary(input: ArrayBuffer | Uint8Array): MeshData {
  assertLittleEndian()
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input)
  const { header, bodyOffset } = readMeshBinaryHeader(bytes)

  // `slice`, not `subarray`: a copy whose buffer starts at 0, so the typed
  // array views in `dequantise` are aligned whatever offset `input` sat at
  // (a node `Buffer` from `readFileSync` usually shares a pooled buffer).
  const region = (byteOffset: number, byteLength: number): Uint8Array => {
    const start = bodyOffset + byteOffset
    if (start + byteLength > bytes.byteLength) {
      throw new Error(`MeshBinary: region ${byteOffset}+${byteLength} runs past the file`)
    }
    return bytes.slice(start, start + byteLength)
  }

  const attributes: MeshDataAttribute[] = header.attributes.map((a) => {
    const count = header.vertexCount * a.itemSize
    if (a.byteLength !== count * BYTES_PER_COMPONENT[a.format]) {
      throw new Error(`MeshBinary: attribute "${a.name}" is ${a.byteLength} bytes, expected ${count * BYTES_PER_COMPONENT[a.format]}`)
    }
    return {
      name: a.name,
      itemSize: a.itemSize,
      format: a.format,
      array: dequantise(region(a.byteOffset, a.byteLength), a.format, count),
    }
  })

  const indexBytes = region(header.index.byteOffset, header.index.byteLength)
  const index =
    header.index.format === 'u16'
      ? new Uint16Array(indexBytes.buffer, indexBytes.byteOffset, header.index.count)
      : new Uint32Array(indexBytes.buffer, indexBytes.byteOffset, header.index.count)

  return { vertexCount: header.vertexCount, attributes, index, groups: header.groups }
}
